/**
 * Gli stati dell'assemblea, come li chiama il backend, con l'etichetta e la pill
 * con cui si mostrano.
 *
 * Il backend usa un enum (`DRAFT`, `CONVENED`, …), il prototipo usava le etichette
 * italiane come valore: la traduzione sta qui, in un posto solo. Le regole di
 * `AZIONI` sono la stessa macchina a stati che il server fa rispettare
 * (`EDITABLE` in assemblies.service.ts) — qui servono a non mostrare bottoni che
 * prenderebbero un 409.
 *
 *   DRAFT ──invio──► CONVENED ──svolta────► MINUTES_DRAFT ──chiusura──► MINUTES_FINAL
 *                        └────deserta─────► DESERTED ──────┘
 */
export const STATI = {
	DRAFT: { label: 'Bozza', pill: 'sid-pill--gray' },
	CONVENED: { label: 'Convocata', pill: 'sid-pill--blue' },
	FIRST_CALL_DESERTED: { label: 'Prima convocazione deserta', pill: 'sid-pill--amber' },
	DESERTED: { label: 'Andata deserta', pill: 'sid-pill--red' },
	MINUTES_DRAFT: { label: 'Verbale in bozza', pill: 'sid-pill--amber' },
	MINUTES_FINAL: { label: 'Verbale firmato', pill: 'sid-pill--green' }
};

export const statoAssemblea = (stato) => STATI[stato] || { label: stato || '—', pill: 'sid-pill--gray' };

/**
 * Cosa si può fare da ciascuno stato, cioè quali icone hanno senso nella riga.
 *
 * `modifica` segue la regola della specifica: dopo l'invio della convocazione i
 * dati non si toccano più. `verbale` è vero da quando la riunione si è svolta (o è
 * andata deserta) fino alla chiusura; `scarica` da quando il verbale esiste.
 */
export const AZIONI = {
	DRAFT: { modifica: true, verbale: false, scarica: false },
	CONVENED: { modifica: false, verbale: false, scarica: false },
	// La prima convocazione è andata deserta ma l'assemblea non è conclusa: si aspetta
	// la seconda, e il verbale non si scrive ancora.
	FIRST_CALL_DESERTED: { modifica: false, verbale: false, scarica: false },
	DESERTED: { modifica: false, verbale: true, scarica: true },
	MINUTES_DRAFT: { modifica: false, verbale: true, scarica: true },
	MINUTES_FINAL: { modifica: false, verbale: false, scarica: true }
};

export const azioniPerStato = (stato) => AZIONI[stato] || AZIONI.CONVENED;

/**
 * Cosa resta scrivibile in ciascuno stato, scheda per scheda.
 *
 * È la copia esatta di `EDITABLE` in `assemblies.service.ts`: il server risponde
 * 409 a chi prova lo stesso, qui serve a non far arrivare l'utente fin lì. `dati`
 * e `convocazione` si bloccano insieme, perché per il backend sono la stessa cosa
 * (la sezione `notice`).
 */
export const REGOLE = {
	DRAFT: {
		dati: true, convocazione: true, presenze: false, verbale: false,
		puoConvocare: true, puoRegistrareEsito: false, puoChiudere: false
	},
	CONVENED: {
		dati: false, convocazione: false, presenze: true, verbale: false,
		puoConvocare: false, puoRegistrareEsito: true, puoChiudere: false
	},
	// Le presenze restano scrivibili: fra la prima e la seconda convocazione passa
	// un'ora e la gente arriva. Il verbale no, perché l'assemblea non è conclusa.
	FIRST_CALL_DESERTED: {
		dati: false, convocazione: false, presenze: true, verbale: false,
		puoConvocare: false, puoRegistrareEsito: true, puoChiudere: false
	},
	DESERTED: {
		dati: false, convocazione: false, presenze: true, verbale: true,
		puoConvocare: false, puoRegistrareEsito: false, puoChiudere: true
	},
	MINUTES_DRAFT: {
		dati: false, convocazione: false, presenze: false, verbale: true,
		puoConvocare: false, puoRegistrareEsito: false, puoChiudere: true
	},
	MINUTES_FINAL: {
		dati: false, convocazione: false, presenze: false, verbale: false,
		puoConvocare: false, puoRegistrareEsito: false, puoChiudere: false
	}
};

// Un'assemblea che non esiste ancora è una bozza a tutti gli effetti.
export const regolePerStato = (stato) => REGOLE[stato] || REGOLE.DRAFT;

/**
 * Il quorum, ricalcolato dai conteggi che il dettaglio porta con sé.
 *
 * `raggiunto` vale solo per la prima convocazione, dove serve la maggioranza degli
 * aventi diritto. In seconda non si calcola niente: lo statuto di solito dichiara
 * l'assemblea valida qualunque sia il numero dei presenti, e dove non lo fa la
 * valutazione è del presidente. Si mostrano i numeri e non si decide, come già per
 * il limite di deleghe per delegato.
 */
export const calcolaQuorum = (counters) => {
	const invitati = counters?.invitees ?? 0;
	const totale = (counters?.present ?? 0) + (counters?.proxies ?? 0);

	return {
		totale,
		invitati,
		percentuale: invitati > 0 ? Math.round((totale / invitati) * 100) : 0,
		raggiunto: totale > invitati / 2
	};
};

export const TIPOLOGIE = {
	ORDINARY: 'Ordinaria',
	EXTRAORDINARY: 'Straordinaria'
};

/** '2026-05-15' -> '15/05/2026'. Il backend parla ISO, l'interfaccia italiano. */
export const dataItaliana = (iso) => {
	if (!iso) return '—';
	const [anno, mese, giorno] = iso.split('-');
	return giorno && mese && anno ? `${giorno}/${mese}/${anno}` : iso;
};

/** L'orario si mostra come intervallo solo se la fine c'è: non sempre si conosce. */
export const orario = (call) => {
	if (!call || !call.timeStart) return '—';
	return call.timeEnd ? `${call.timeStart}–${call.timeEnd}` : call.timeStart;
};
